import { motion } from 'framer-motion';
import { Radar, Bot, UserCheck, Infinity, FileText, ArrowRight } from 'lucide-react';

const Section3Benefits = () => {
  const benefits = [
    {
      icon: Radar,
      title: '24/7 Signal Monitoring',
      description: 'Round-the-clock tracking of all 50+ signals. We catch issues while you sleep.',
      color: 'emerald'
    },
    {
      icon: Bot,
      title: 'Nora AI Agent',
      description: 'Our most advanced AI analyzes your account patterns and predicts enforcement risks before they happen.',
      color: 'purple'
    },
    {
      icon: UserCheck,
      title: 'Human Account Specialist',
      description: 'A dedicated account health expert who knows your account and responds when things go wrong.',
      color: 'blue'
    },
    {
      icon: Infinity,
      title: 'Unlimited Plan Options',
      description: 'Whether you need monitoring only or full white-glove protection, we have a plan that fits.',
      color: 'orange'
    },
    {
      icon: FileText,
      title: 'Detailed Audit Reports',
      description: 'Comprehensive account health audits with prioritized, actionable items you can fix today.',
      color: 'red'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', badge: 'bg-emerald-600' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', badge: 'bg-purple-600' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-600' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', badge: 'bg-orange-600' },
      red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', badge: 'bg-red-600' }
    };
    return colors[color];
  };

  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="section3-benefits">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-purple-600">
            What You Get with Section3.ai
          </p>
          <h2 className="font-display text-slate-800 text-3xl sm:text-4xl lg:text-5xl mt-3">
            Protection That Actually Works
          </h2>
          <p className="text-slate-500 text-lg mt-4 max-w-2xl mx-auto">
            Not just alerts — real protection backed by AI and human expertise.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => {
            const colorClasses = getColorClasses(benefit.color);
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl border-2 ${colorClasses.border} p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
              >
                <span className={`w-14 h-14 rounded-xl ${colorClasses.bg} flex items-center justify-center`}>
                  <benefit.icon className={`w-7 h-7 ${colorClasses.icon}`} strokeWidth={1.75} />
                </span>
                <h3 className="font-display text-slate-800 text-xl mt-6">
                  {benefit.title}
                </h3>
                <p className="text-slate-500 leading-relaxed mt-3">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <a
            href="https://amazonworkshop.section3.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 text-lg font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-full transition-all hover:scale-105 shadow-lg shadow-purple-500/30"
            data-testid="section3-cta"
          >
            Learn More in Free Workshop
            <ArrowRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Section3Benefits;
